import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, PieChart, Pie, Cell } from 'recharts';

const COLORS = ['#4F46E5', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

const tooltipStyle = { 
  backgroundColor: '#1F2937', 
  border: '1px solid #374151',
  borderRadius: '8px',
  color: '#F3F4F6'
};

export function WorkloadComparisonChart({ data, t }) {
  if (!data || data.length === 0) return (
    <div className="text-center py-8 text-muted-foreground">
      {t('reports.noData')}
    </div>
  );
  
  const chartData = data.map(w => ({
    name: w.worker_name?.split(' ')[0] || 'Worker',
    scheduled: w.scheduled_hours || 0,
    worked: w.worked_hours || 0,
    capacity: w.available_capacity || 0,
    utilization: w.utilization || 0,
    color: w.color || '#4F46E5'
  }));

  return (
    <ResponsiveContainer width="100%" height={350}>
      <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
        <XAxis dataKey="name" tick={{ fill: '#9CA3AF', fontSize: 12 }} />
        <YAxis tick={{ fill: '#9CA3AF', fontSize: 12 }} label={{ value: t('reports.hours'), angle: -90, position: 'insideLeft', fill: '#9CA3AF' }} />
        <Tooltip 
          contentStyle={tooltipStyle}
          formatter={(value, name) => [
            `${value} ${t('reports.hours')}`,
            name === 'scheduled' ? t('reports.scheduledHours') : 
            name === 'worked' ? t('reports.workedHours') : 
            t('reports.availableCapacity')
          ]}
        />
        <Legend 
          formatter={(value) => 
            value === 'scheduled' ? t('reports.scheduledHours') : 
            value === 'worked' ? t('reports.workedHours') : 
            t('reports.availableCapacity')
          }
        />
        <Bar dataKey="capacity" fill="#374151" radius={[4, 4, 0, 0]} name="capacity" />
        <Bar dataKey="scheduled" fill="#F59E0B" radius={[4, 4, 0, 0]} name="scheduled" />
        <Bar dataKey="worked" fill="#10B981" radius={[4, 4, 0, 0]} name="worked" />
      </BarChart>
    </ResponsiveContainer>
  );
}

export function WorkloadChart({ data }) {
  if (!data || data.length === 0) return null;
  
  const chartData = data.map(w => ({
    name: w.worker_name?.split(' ')[0] || 'Worker',
    hours: w.hours_worked || 0,
    orders: w.orders_completed || 0,
    revenue: w.revenue || 0,
    color: w.color || '#4F46E5'
  }));

  return (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
        <XAxis dataKey="name" tick={{ fill: '#9CA3AF', fontSize: 12 }} />
        <YAxis tick={{ fill: '#9CA3AF', fontSize: 12 }} />
        <Tooltip 
          contentStyle={tooltipStyle}
          formatter={(value, name) => [
            name === 'revenue' ? `$${value}` : value,
            name === 'hours' ? 'Hours' : name === 'orders' ? 'Orders' : 'Revenue'
          ]}
        />
        <Bar dataKey="hours" fill="#4F46E5" radius={[4, 4, 0, 0]} />
        <Bar dataKey="orders" fill="#10B981" radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

export function ServicesPieChart({ data }) {
  if (!data || data.length === 0) return null;
  
  const chartData = data.filter(s => s.revenue > 0).map(s => ({
    name: s.service_name,
    value: s.revenue,
    color: s.color
  }));

  if (chartData.length === 0) return null;

  return (
    <ResponsiveContainer width="100%" height={250}>
      <PieChart>
        <Pie
          data={chartData}
          cx="50%"
          cy="50%"
          innerRadius={60}
          outerRadius={80}
          paddingAngle={5}
          dataKey="value"
          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
          labelLine={false}
        >
          {chartData.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={entry.color || COLORS[index % COLORS.length]} />
          ))}
        </Pie>
        <Tooltip 
          formatter={(value) => [`$${value.toFixed(0)}`, 'Revenue']}
          contentStyle={tooltipStyle}
        />
      </PieChart>
    </ResponsiveContainer>
  );
}

export function OrdersByDateChart({ data }) {
  if (!data || data.length === 0) return null;
  
  const chartData = data.map(d => ({
    date: d.date?.slice(5) || '',
    orders: d.count || 0,
    revenue: d.revenue || 0
  }));

  return (
    <ResponsiveContainer width="100%" height={250}>
      <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
        <XAxis dataKey="date" tick={{ fill: '#9CA3AF', fontSize: 10 }} />
        <YAxis tick={{ fill: '#9CA3AF', fontSize: 12 }} />
        <Tooltip 
          contentStyle={tooltipStyle}
          formatter={(value, name) => [
            name === 'revenue' ? `$${value}` : value,
            name === 'orders' ? 'Orders' : 'Revenue'
          ]}
        />
        <Bar dataKey="orders" fill="#4F46E5" radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

export function SimpleBarChart({ data, valueKey, labelKey, colorKey, maxValue }) {
  if (!data || data.length === 0) return null;
  const max = maxValue || Math.max(...data.map(d => d[valueKey] || 0));
  
  return (
    <div className="space-y-3">
      {data.map((item, idx) => (
        <div key={idx} className="space-y-1">
          <div className="flex justify-between text-sm">
            <span className="truncate max-w-[200px]">{item[labelKey]}</span>
            <span className="font-mono font-medium">${item[valueKey]?.toFixed(0) || 0}</span>
          </div>
          <div className="h-3 bg-muted rounded-full overflow-hidden">
            <div 
              className="h-full rounded-full transition-all duration-500"
              style={{ 
                width: `${max > 0 ? (item[valueKey] / max) * 100 : 0}%`,
                backgroundColor: item[colorKey] || '#4F46E5'
              }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}
